import { useTranslation } from "react-i18next";
import { Badge, Button, confirmDelete } from "../../../components/ui";
import { getTimeAgo } from "../../../utils/getDate";
import { useShipmentTrackingLink } from "../../../features/shipments/trackingLink";
import {
  isShipmentUnseenByDriver,
  lastShipmentEvent,
  shipmentEventTypeOf,
  shipmentStage,
  type Shipment,
} from "../../../features/shipments/types";
import { useShipmentUnit } from "../hooks/useShipmentUnit";
import { STAGE_DOTS, STAGE_TONES } from "../statusTones";

interface EnvioCardProps {
  shipment: Shipment;
  onOpen: () => void;
  onCopied: () => void;
  onCopyError: (error: unknown) => void;
  onDelete: (shipment: Shipment) => void;
}

export function EnvioCard({
  shipment,
  onOpen,
  onCopied,
  onCopyError,
  onDelete,
}: EnvioCardProps) {
  const { t } = useTranslation("envios");
  const unit = useShipmentUnit(shipment.actual_device_id);
  const trackingLink = useShipmentTrackingLink(shipment, { watch: false });

  const stage = shipmentStage(shipment);
  const lastEvent = lastShipmentEvent(shipment);
  const eventType = shipmentEventTypeOf(shipment);

  const statusLabel = eventType
    ? t(`eventTypes.${eventType}`)
    : t(`stages.${stage}`);

  const lastUpdate = lastEvent?.date
    ? getTimeAgo(Math.floor(new Date(lastEvent.date).getTime() / 1000))
    : t("detail.awaitingFirstEvent");

  const route =
    [shipment.origin.address, shipment.destination.address].filter(Boolean).join(" → ") ||
    t("card.noRoute");

  const handleCopy = async () => {
    try {
      const url = await trackingLink.ensure();
      await navigator.clipboard.writeText(url);
      onCopied();
    } catch (error) {
      onCopyError(error);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDelete({
      title: t("list.deleteTitle"),
      text: t("list.deleteText", { name: shipment.tracking_number }),
      cancelText: t("form.cancel"),
    });
    if (confirmed) onDelete(shipment);
  };

  return (
    <div
      onClick={onOpen}
      className="relative bg-card border border-line rounded-2xl p-5 cursor-pointer transition-colors hover:border-primary-500/50 hover:bg-primary-500/5"
    >
      <span
        className={`absolute top-5 right-5 w-2.5 h-2.5 rounded-full ${STAGE_DOTS[stage]}`}
      />

      <div className="flex items-start gap-3 mb-3 pr-5">
        <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-500 flex items-center justify-center shrink-0">
          <i className="ri-archive-line text-lg" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold text-content truncate">
            {shipment.shipment_name}
          </h3>
          <p className="text-[11px] font-mono text-content-muted tracking-wide mt-0.5 truncate">
            {shipment.tracking_number}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[12.5px] text-content-secondary mb-3">
        <i className="ri-map-pin-line text-sm shrink-0" />
        <span className="truncate">{route}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-subtle rounded-lg px-3 py-2 text-center">
          <div className="text-[11px] text-content-muted">{t("detail.lastUpdate")}</div>
          <div className="text-[13.5px] font-bold text-content mt-0.5 truncate">
            {lastUpdate}
          </div>
        </div>
        <div className="bg-subtle rounded-lg px-3 py-2 text-center">
          <div className="text-[11px] text-content-muted">{t("card.lastEvent")}</div>
          <div className="text-[13.5px] font-bold text-content mt-0.5 truncate">
            {statusLabel}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        <Badge tone={STAGE_TONES[stage]}>{statusLabel}</Badge>
        {shipment.transportation_type && (
          <Badge tone="neutral">
            {t(`transport.${shipment.transportation_type}`, {
              defaultValue: shipment.transportation_type,
            })}
          </Badge>
        )}
        {isShipmentUnseenByDriver(shipment) && (
          <Badge tone="warning">
            <i className="ri-route-line mr-1 text-[10px]" />
            {t("tripPicker.cardBadge")}
          </Badge>
        )}
        {unit?.plate && (
          <Badge tone="neutral">
            <i className="ri-car-line mr-1 text-[10px]" />
            {unit.plate}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          icon={<i className="ri-map-pin-line" />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="flex-1"
        >
          {t("buttons.actualizar")}
        </Button>
        {trackingLink.hasLink && (
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-link" />}
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="flex-1"
          >
            {t("buttons.copiarLink")}
          </Button>
        )}
        <Button
          variant="dangerSoft"
          size="sm"
          iconOnly
          icon={<i className="ri-delete-bin-line" />}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          title={t("buttons.delete")}
          aria-label={t("buttons.delete")}
          className="shrink-0"
        />
      </div>
    </div>
  );
}
